/**
 * 达人订单详情页（C端用户侧）
 *
 * 路由：/order/detail/expert?id={orderId}
 * 数据源：GET /api/massage/order/detail/{id} （C端 BLW 接口）
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog } from "antd-mobile";
import { type StaffOrderDetail } from "@/api/staff";
import { cancelExpertOrder, getExpertOrderDetail } from "@/api/expert-order";
import { toastError, toastSuccess } from "@/utils/toast";

// ── Hooks & Components ──────────────────────────────
import {
  getServiceSteps,
  getStatusAlert,
  type AfterSaleState,
} from "./hooks";

// ── 页面组件 ─────────────────────────────────────────
import DetailHeader from "./components/DetailHeader";
import OrderInfo from "./components/OrderInfo";
import PriceDetail from "./components/PriceDetail";
import StatusAlert from "./components/StatusAlert";
import PunchCard from "./components/PunchCard";
import ActionBar from "./components/ActionBar";
import VoucherServiceSection from "../components/VoucherServiceSection";

// ── 共享弹窗 ─────────────────────────────────────────
import {
  AfterSaleModal,
  ReviewModal,
  InvoiceModal,
} from "@/components/modals";

export default function OrderDetailExpertPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id");

  // ── 状态 ────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<StaffOrderDetail | null>(null);
  const [showPriceDetail, setShowPriceDetail] = useState(false);

  // 弹窗状态
  const [afterSaleVisible, setAfterSaleVisible] = useState(false);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [invoiceVisible, setInvoiceVisible] = useState(false);

  // ── 加载订单详情（C端接口：api/massage/order/detail） ─────────
  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    getExpertOrderDetail(Number(orderId))
      .then((data) => {
        setOrder(data as any);
      })
      .catch((err) => {
        console.error("[ExpertOrderDetail] 加载失败:", err);
        toastError(err?.message || "加载订单详情失败");
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  // ── 刷新订单（操作后重新获取） ──────────────────────
  const reloadOrder = () => {
    if (!orderId) return;
    getExpertOrderDetail(Number(orderId))
      .then((data) => setOrder(data as any))
      .catch((err) => toastError(err?.message || "刷新失败"));
  };

  // ── 派生数据 ────────────────────────────────────────
  const st = order?.status ?? 0;
  const serviceSteps = getServiceSteps(st, order?.is_depart);
  const statusAlert = getStatusAlert(st);

  /** 售后/开票互斥状态 */
  const afterSaleState: AfterSaleState =
    (order?.refund_id ?? 0) > 0
      ? "refund"
      : "none";

  const payPrice = order ? Number(order.pay_price) : 0;
  const originalPrice = order ? Number(order.original_price) : 0;
  // 使用接口返回的优惠券+折扣字段精确计算，兜底用原价-实付
  const couponPrice = order ? Number(order.coupon_price) : 0;
  const discountPrice = order ? Number(order.discount_price) : 0;
  const discount = (couponPrice + discountPrice) > 0 ? couponPrice + discountPrice
    : originalPrice > payPrice ? originalPrice - payPrice : 0;

  // ── 凭证与服务（始终显示3项，副标题根据 status 动态变化） ──
  const voucherItems: { icon: string; label: string; sub: string; color: string; path: string }[] = [
    {
      icon: "ri-file-text-line",
      label: "电子合同",
      sub: st <= 0 ? "待签约"
        : st === 13 ? "待签署"
        : st >= 20 ? "已签署"
        : "待签约",
      color: "#2563EB",
      path: "/contracts",
    },
    {
      icon: "ri-bill-line",
      label: "电子发票",
      sub: st >= 40 || st === -10
        ? "可申请开具"
        : "服务完成后可申请",
      color: "#D97706",
      path: "/invoices",
    },
    {
      icon: "ri-customer-service-2-line",
      label: "在线客服",
      sub: "7×24小时",
      color: "#7C3AED",
      path: "/messages",
    },
  ];

  // ── 操作处理 ────────────────────────────────────────
  const handleAction = async (action: string) => {
    if (!order) return;

    switch (action) {
      case "pay": {
        // 跳转支付页
        navigate("/payment", {
          state: {
            orderNo: order.ordersn,
            totalAmount: String(payPrice),
            type: "guide",
          },
        });
        break;
      }

      case "cancel": {
        Dialog.show({
          content: `确认取消该达人订单？`,
          closeOnAction: true,
          actions: [
            [
              {
                key: "cancel",
                text: "再想想",
                style: { color: "#FF6B35", fontWeight: 500 },
                onClick: () => {},
              },
              {
                key: "confirm",
                text: "确认取消",
                bold: true,
                style: { color: "#FF6B35", fontWeight: 600 },
                onClick: async () => {
                  try {
                    await cancelExpertOrder(order.id);
                    toastSuccess("订单已取消");
                    reloadOrder();
                  } catch (err: any) {
                    toastError(err?.message || "取消失败");
                  }
                },
              },
            ],
          ],
        });
        break;
      }

      case "refund": {
        setAfterSaleVisible(true);
        break;
      }

      case "comment": {
        setReviewVisible(true);
        break;
      }

      case "invoice": {
        setInvoiceVisible(true);
        break;
      }

      case "reorder": {
        navigate(-1);
        break;
      }

      case "service": {
        // 联系客服 / 打开聊天
        window.location.href = "/messages";
        break;
      }
    }
  };

  // ── Loading ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <i className="ri-loader-4-line animate-spin text-4xl" style={{ color: "#FF6B35" }} />
        <p className="mt-3 text-sm text-gray-400">加载中...</p>
      </div>
    );
  }

  // ── 无数据 ──────────────────────────────────────────
  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <i className="ri-file-warning-line text-5xl text-gray-300" />
        <p className="mt-3 text-sm text-gray-400">订单不存在</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-6 py-2 rounded-full text-sm text-white"
          style={{ background: "#FF6B35" }}
        >
          返回
        </button>
      </div>
    );
  }

  // ── 主渲染 ──────────────────────────────────────────
  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+96px)]"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* 顶部 Header + 6步流程进度条 */}
      <DetailHeader data={order} serviceSteps={serviceSteps} payPrice={payPrice} />

      {/* 状态提醒条 */}
      <StatusAlert
        visible={!!statusAlert}
        text={statusAlert?.text ?? ""}
        type={statusAlert?.type ?? "info"}
      />

      {/* 订单基本信息 */}
      <OrderInfo data={order} />

      {/* 达人打卡服务时间线（始终显示，无数据时展示空白待打卡节点） */}
      <PunchCard data={order} />

      {/* 费用明细 */}
      <PriceDetail
        originalPrice={originalPrice}
        payPrice={payPrice}
        discount={discount}
        couponPrice={couponPrice}
        discountPrice={discountPrice}
        showPriceDetail={showPriceDetail}
        onToggle={() => setShowPriceDetail(!showPriceDetail)}
      />

      {/* 凭证与服务（电子合同 / 电子发票 / 在线客服） */}
      <VoucherServiceSection
        onNavigate={(path) => navigate(path)}
        items={voucherItems}
      />

      {/* 底部操作按钮栏 */}
      <ActionBar
        orderStatus={st}
        afterSaleState={afterSaleState}
        hasCommented={(order.is_comment ?? 0) === 1}
        onAction={handleAction}
      />

      {/* 售后弹窗 */}
      <AfterSaleModal
        visible={afterSaleVisible}
        productName={`${order.staff_name || "达人"}·${order.item_name || "达人服务"}`}
        orderId={order.id}
        orderType="guide"
        orderAmount={payPrice}
        onClose={() => setAfterSaleVisible(false)}
        onSubmitSuccess={() => {
          setAfterSaleVisible(false);
          reloadOrder();
        }}
      />

      {/* 评价弹窗 */}
      <ReviewModal
        visible={reviewVisible}
        orderId={order.id}
        productId={order.service_staff_id}
        productName={`${order.staff_name || "达人"}·${order.city || ""}达人服务`}
        productType="guide"
        onClose={() => setReviewVisible(false)}
        onSubmitSuccess={() => {
          setReviewVisible(false);
          reloadOrder();
        }}
      />

      {/* 开票弹窗 */}
      {invoiceVisible && (
        <InvoiceModal
          orderId={order.id}
          productType="guide"
          productTitle={`${order.staff_name || "达人"}·${order.item_name || "达人服务"}`}
          productSubtitle={[
            order.accept_order_type === 2 ? `${order.num}天` : `${order.num}${order.unit_name || "小时"}`,
            order.book_time_desc,
            order.city,
          ]
            .filter(Boolean)
            .join(" · ")}
          invoiceAmount={payPrice}
          onClose={() => setInvoiceVisible(false)}
          onSubmitSuccess={() => {
            setInvoiceVisible(false);
            reloadOrder();
          }}
        />
      )}
    </div>
  );
}
